import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useResponsive } from 'contexts/ResponsiveContext';
import type { WipRecordI } from 'declarations/interfaces/wip';
import { CloseIcon, SearchIcon } from 'icons';
import {
  BtnFlexContainerStyled,
  ClearBtnIconStyled,
  ClearBtnWrapStyled,
  FilterChipsRowStyled,
  FlexContainerStyled,
  HeaderStyled,
  InputStyled,
  MobileHeaderRowStyled,
  SearchAndFiltersRowStyled,
  SearchBarWrapStyled,
} from 'pages/TicketsPage/components/Header/Header.styled';
import type { WipAppliedFiltersI } from 'reduxStore/features/wip/wipSlice';
import {
  clearWipAppliedFilters,
  selectWipAppliedFilters,
  selectWipSearchTerm,
  setWipSearchTerm,
} from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';

import { getWipChirpTicketToolbarState } from '../../helpers/wipChirpTicketToolbar';
import WIPFilterChips from '../Filters/WIPFilterChips';
import WIPFiltersDropdown from '../Filters/WIPFiltersDropdown';

type WipHeaderPropsT = {
  isCardsRowExpanded?: boolean;
  onToggleCardsRow?: () => void;
  onOpenColumnPicker: () => void;
  wipItemsSelected: readonly WipRecordI[];
  onCreateChirpTicketClick: () => void;
};

const SEARCH_DEBOUNCE_MS = 300;

const countActiveFilters = (filters: WipAppliedFiltersI): number => {
  let count = 0;
  count += filters.roofTopIds.length;
  count += filters.oemFamilyIds.length;
  count += filters.wipStatuses.length;
  count += filters.claimStatuses.length;
  count += filters.repairOrderStatuses.length;
  if (filters.backlog !== null) count += 1;
  if (filters.createdAtFrom || filters.createdAtTo) count += 1;
  return count;
};

const Header: FC<WipHeaderPropsT> = ({
  isCardsRowExpanded = true,
  onToggleCardsRow,
  onOpenColumnPicker,
  wipItemsSelected,
  onCreateChirpTicketClick,
}) => {
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const reduxSearchTerm = useAppSelector(selectWipSearchTerm);
  const appliedFilters = useAppSelector(selectWipAppliedFilters);

  const [searchText, setSearchText] = useState<string>(reduxSearchTerm);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const filtersAnchorRef = useRef<HTMLButtonElement>(null);

  const cancelPendingSync = useCallback((): void => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const scheduleDebouncedSync = useCallback(
    (value: string): void => {
      cancelPendingSync();
      debounceTimerRef.current = setTimeout((): void => {
        dispatch(setWipSearchTerm(value));
      }, SEARCH_DEBOUNCE_MS);
    },
    [cancelPendingSync, dispatch]
  );

  useEffect(
    (): (() => void) => (): void => {
      cancelPendingSync();
    },
    [cancelPendingSync]
  );

  const onChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSearchText(event.target.value);
      scheduleDebouncedSync(event.target.value);
    },
    [scheduleDebouncedSync]
  );

  const resetSearchTerm = useCallback((): void => {
    setSearchText('');
    cancelPendingSync();
    dispatch(setWipSearchTerm(''));
  }, [cancelPendingSync, dispatch]);

  const activeFilterCount = useMemo(
    (): number => countActiveFilters(appliedFilters),
    [appliedFilters]
  );

  const handleOpenFilters = useCallback((): void => {
    setFiltersAnchor(filtersAnchorRef.current);
  }, []);

  const handleCloseFilters = useCallback((): void => {
    setFiltersAnchor(null);
  }, []);

  const handleClearFilters = useCallback((): void => {
    dispatch(clearWipAppliedFilters());
  }, [dispatch]);

  const { disabled: isCreateChirpDisabled, tooltip: createChirpTooltip } = useMemo(
    () => getWipChirpTicketToolbarState(wipItemsSelected),
    [wipItemsSelected]
  );

  const handleOpenCreateChirpTicket = useCallback((): void => {
    if (isCreateChirpDisabled) return;
    onCreateChirpTicketClick();
  }, [isCreateChirpDisabled, onCreateChirpTicketClick]);

  const searchBar = (
    <SearchBarWrapStyled title="Search by RO number">
      <InputStyled
        id="wipSearchTerm"
        name="wipSearchTerm"
        icon={<SearchIcon />}
        placeholder="Search by RO number"
        value={searchText}
        onChange={onChangeInput}
        bg="grey"
      />
      {searchText && (
        <ClearBtnWrapStyled justify="center">
          <ClearBtnIconStyled
            size="md"
            variant="transparent"
            icon={<CloseIcon size="xs" />}
            onClick={resetSearchTerm}
          />
        </ClearBtnWrapStyled>
      )}
    </SearchBarWrapStyled>
  );

  const filterButtons = (
    <>
      <Tooltip title="Filters" arrow>
        <IconButton
          ref={filtersAnchorRef}
          aria-label="Filters"
          size="small"
          onClick={handleOpenFilters}
        >
          <Badge badgeContent={activeFilterCount} color="primary">
            <FilterListIcon sx={{ color: (theme): string => theme.palette.primary.main }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear filters" arrow>
        <span>
          <IconButton
            aria-label="Clear filters"
            size="small"
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
          >
            <FilterListOffIcon sx={{ color: (theme): string => theme.palette.primary.main }} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const actionButtons = (
    <BtnFlexContainerStyled>
      <Tooltip title={createChirpTooltip} arrow>
        <span>
          <IconButton
            aria-label="Create Chirp ticket"
            disabled={isCreateChirpDisabled}
            onClick={handleOpenCreateChirpTicket}
            sx={{ color: (theme): string => theme.palette.primary.main }}
          >
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Customize table columns" arrow>
        <IconButton
          aria-label="Customize columns"
          onClick={onOpenColumnPicker}
          sx={{ color: (theme): string => theme.palette.primary.main }}
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      {onToggleCardsRow != null && (
        <Tooltip title={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'} arrow>
          <IconButton
            aria-label={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
            onClick={onToggleCardsRow}
            sx={{ color: (theme): string => theme.palette.primary.main }}
          >
            {isCardsRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      )}
    </BtnFlexContainerStyled>
  );

  if (isMobile) {
    return (
      <>
        <HeaderStyled>
          <MobileHeaderRowStyled>
            {searchBar}
            {filterButtons}
            {actionButtons}
          </MobileHeaderRowStyled>
          <FilterChipsRowStyled>
            <WIPFilterChips />
          </FilterChipsRowStyled>
          <Divider />
        </HeaderStyled>
        <WIPFiltersDropdown
          anchorEl={filtersAnchor}
          isOpen={Boolean(filtersAnchor)}
          onClose={handleCloseFilters}
        />
      </>
    );
  }

  return (
    <>
      <HeaderStyled>
        <FlexContainerStyled>
          <SearchAndFiltersRowStyled>
            {searchBar}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
              {filterButtons}
              <Box sx={{ ml: 1, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <WIPFilterChips />
              </Box>
            </Box>
          </SearchAndFiltersRowStyled>
          {actionButtons}
        </FlexContainerStyled>
        <Divider />
      </HeaderStyled>
      <WIPFiltersDropdown
        anchorEl={filtersAnchor}
        isOpen={Boolean(filtersAnchor)}
        onClose={handleCloseFilters}
      />
    </>
  );
};

export default Header;
